// ResponseTemplates.js - Fixed to respect config_id filter properly
import React, { useState, useEffect, useCallback } from "react";
import { useLocation, useHistory } from "react-router-dom";
import styles from "./ResponseTemplates.module.css";
import { FaInfoCircle } from "react-icons/fa";

const backend_api_base_url = `${process.env.REACT_APP_SL_BE_BASE_ENV_PART}/${process.env.REACT_APP_STAGE_ENV}`;

const ResponseTemplates = ({ appData }) => {
  const location = useLocation();
  const history = useHistory();
  const queryParams = new URLSearchParams(location.search);

  // Get query parameters
  const configId = queryParams.get('config_id') || '';
  const kbId = queryParams.get('kb_id') || '';
  const statusCode = queryParams.get('status_code') || '';

  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterType, setFilterType] = useState('all'); // 'all', 'local', 'default_app', 'default_global'
  const [showTooltip, setShowTooltip] = useState(null);

  const region = appData?.region?.toLowerCase() || "us";
  const stackPrefix = process.env.REACT_APP_STAGE_ENV || "dev";
  const token = localStorage.getItem("token") || "";
  const domainBase = process.env.REACT_APP_SL_BE_BASE_ENV_PART;

  console.log("domainBasedddddd on ResponseTemplate screen",domainBase)
  const fetchAllTemplates = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const baseUrl = `https://${region}.autobot2.${domainBase}/${stackPrefix}/final_response_versions`;

      // Determine which entry types to fetch based on available params
      const entryTypes = [];

      // If config_id is provided, ONLY fetch local_app_level for that specific config
      // This is the key fix: when config_id is present, we should ONLY fetch local_app_level
      if (configId) {
        entryTypes.push('local_app_level');
      }

      // If kb_id is provided (and no config_id), fetch default_app_level for that kb
      if (kbId && !configId) {
        entryTypes.push('default_app_level');
      }

      // Only fetch default_global_level when NO config_id is provided
      // (global templates should only show when there's no specific config filter)
      if (!configId) {
        entryTypes.push('default_global_level');
      }

      // If we don't have any filters, fetch all types
      if (!configId && !kbId) {
        if (!entryTypes.includes('default_app_level')) entryTypes.push('default_app_level');
        if (!entryTypes.includes('local_app_level')) entryTypes.push('local_app_level');
        if (!entryTypes.includes('default_global_level')) entryTypes.push('default_global_level');
      }

      console.log('[ResponseTemplates] Fetching entry types:', entryTypes);
      console.log('[ResponseTemplates] Config ID:', configId);
      console.log('[ResponseTemplates] KB ID:', kbId);

      // Fetch each entry type in parallel
      const fetchPromises = entryTypes.map(async (entryType) => {
        let url = `${baseUrl}?entry_type=${entryType}`;
        if (statusCode) {
          url += `&status_code=${statusCode}`;
        }
        // When fetching local_app_level, we need to filter by config_id
        if (entryType === 'local_app_level' && configId) {
          // The API should filter by config_id - if the endpoint supports it
          // If not, we'll filter after fetching
          url += `&config_id=${configId}`;
        }
        if (entryType === 'default_app_level' && kbId && !configId) {
          url += `&kb_id=${kbId}`;
        }

        try {
          console.log(`[ResponseTemplates] Fetching ${entryType} from:`, url);
          const response = await fetch(url, {
            headers: {
              "Authorization": token,
              "Content-Type": "application/json"
            }
          });
          if (response.ok) {
            const data = await response.json();
            console.log(`[ResponseTemplates] ${entryType} response:`, data);
            return {
              type: entryType,
              items: data.items || [],
              success: true
            };
          }
          console.error(`[ResponseTemplates] Failed to fetch ${entryType}:`, response.status);
          return { type: entryType, items: [], success: false };
        } catch (error) {
          console.error(`Error fetching ${entryType}:`, error);
          return { type: entryType, items: [], success: false };
        }
      });

      const results = await Promise.all(fetchPromises);

      // Combine all items and deduplicate by id
      const allItems = [];
      const seenIds = new Set();

      results.forEach(result => {
        // Additional filtering for local_app_level to ensure it matches the config_id
        let itemsToAdd = result.items;
        if (result.type === 'local_app_level' && configId) {
          itemsToAdd = result.items.filter(item => item.config_id === configId);
          console.log(`[ResponseTemplates] Filtered local_app_level items for config ${configId}:`, itemsToAdd.length);
        }

        itemsToAdd.forEach(item => {
          if (!seenIds.has(item.id)) {
            seenIds.add(item.id);
            item._type = result.type;
            allItems.push(item);
          }
        });
      });

      // Sort: local_app_level first, then default_app_level, then default_global_level
      const typeOrder = { local_app_level: 0, default_app_level: 1, default_global_level: 2 };
      allItems.sort((a, b) => (typeOrder[a._type] || 99) - (typeOrder[b._type] || 99));

      setTemplates(allItems);

      if (allItems.length === 0) {
        setError('No templates found matching your criteria');
      } else {
        setError(null);
      }

    } catch (error) {
      console.error("Error fetching templates:", error);
      setError('Failed to load templates');
    } finally {
      setLoading(false);
    }
  }, [region, stackPrefix, token, domainBase, configId, kbId, statusCode]);

  useEffect(() => {
    fetchAllTemplates();
  }, [fetchAllTemplates]);

  const getTypeLabel = (type) => {
    const labels = {
      local_app: '📱 Local App',
      local_app_all: '📱 Local App',
      default_app: '🌐 Default App',
      default_global: '🌍 Global'
    };
    return labels[type] || '📄 Template';
  };

  const getTypeColor = (type) => {
    const colors = {
      local_app: '#4a6cf7',
      local_app_all: '#4a6cf7',
      default_app: '#38a169',
      default_global: '#e53e3e'
    };
    return colors[type] || '#718096';
  };

  const getStatusBadgeColor = (statusCode) => {
    if (statusCode >= 200 && statusCode < 300) return '#48bb78';
    if (statusCode >= 400 && statusCode < 500) return '#ed8936';
    if (statusCode >= 500) return '#fc8181';
    return '#a0aec0';
  };

  // ResponseTemplates.js - Updated handleTemplateClick function
  const handleTemplateClick = (template) => {
    // Open in Preview Tester with the template details
    const params = new URLSearchParams({
      scenario: 'local_app_level',
      config_id: template.config_id || configId,
      kb_id: template.kb_id || kbId,
      status_code: template.status_code,
      // ✅ Pass the id (req_uid) to fetch the template from API
      id: template.id,
      // ✅ Also pass entry_type so we know where to fetch from
      entry_type: template._entry_type || 'local_app_level',
    });

    // ❌ REMOVE this entire block - we don't want to pass template in URL anymore
    // const templateStr = typeof template.resp_template === 'object' 
    //   ? JSON.stringify(template.resp_template) 
    //   : template.resp_template || '';
    // 
    // if (templateStr && templateStr.length < 500) {
    //   params.set('template', templateStr);
    // }

    history.push(`/response_format_setter?${params.toString()}`);
  };

  const handleCreateTemplate = () => {
    // Open playground with current context
    const params = new URLSearchParams({
      scenario: 'local_app_level',
      config_id: configId,
      kb_id: kbId,
      template: '{{ response_data | dump | safe }}'
    });
    window.open(`/response_format_setter?${params.toString()}`, '_blank');
  };

  const filteredTemplates = filterType === 'all'
    ? templates
    : templates.filter(t => t._type === filterType);

  const typeCounts = templates.reduce((acc, t) => {
    acc[t._type] = (acc[t._type] || 0) + 1;
    return acc;
  }, {});

  // Info tooltip component
  const InfoTooltip = ({ id, text }) => (
    <span
      style={{
        position: 'relative',
        display: 'inline-block',
        marginLeft: '6px',
        cursor: 'help'
      }}
      onMouseEnter={() => setShowTooltip(id)}
      onMouseLeave={() => setShowTooltip(null)}
    >
      <FaInfoCircle
        size={14}
        style={{
          color: '#718096',
          verticalAlign: 'middle'
        }}
      />
      {showTooltip === id && (
        <div style={{
          position: 'absolute',
          bottom: 'calc(100% + 8px)',
          left: '50%',
          transform: 'translateX(-50%)',
          backgroundColor: '#2d3748',
          color: 'white',
          padding: '8px 12px',
          borderRadius: '6px',
          fontSize: '12px',
          maxWidth: '280px',
          width: 'max-content',
          zIndex: 100,
          boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
          textAlign: 'left',
          lineHeight: '1.4'
        }}>
          {text}
          <div style={{
            position: 'absolute',
            top: '100%',
            left: '50%',
            transform: 'translateX(-50%)',
            border: '6px solid transparent',
            borderTopColor: '#2d3748'
          }} />
        </div>
      )}
    </span>
  );

  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <div className={styles.loadingSpinner}></div>
        <h3>Loading response templates...</h3>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.headerContent}>
          <h1 className={styles.title}>
            📋 Response Message Templates
            <InfoTooltip
              id="header-info"
              text="These templates control what message the user sees after an API call. Each template can use the API's response data to create a personalized message."
            />
          </h1>
          <p className={styles.subtitle}>
            Browse and manage response message templates that format your API responses for users
            {configId && ` for Config: ${configId.substring(0, 12)}...`}
            {kbId && !configId && ` for KB: ${kbId}`}
          </p>
        </div>

      </div>

      {/* Filters */}
      <div className={styles.filterBar}>
        <div className={styles.filterGroup}>
          <span className={styles.filterLabel}>
            Filter by type:
            <InfoTooltip
              id="filter-info"
              text="Templates can be scoped to different levels: Local App (specific to this API), Default App (shared across the app), or Global (shared across all apps)."
            />
          </span>
          <button
            className={`${styles.filterButton} ${filterType === 'all' ? styles.activeFilter : ''}`}
            onClick={() => setFilterType('all')}
          >
            All ({templates.length})
          </button>
          {Object.entries(typeCounts).map(([type, count]) => (
            <button
              key={type}
              className={`${styles.filterButton} ${filterType === type ? styles.activeFilter : ''}`}
              onClick={() => setFilterType(type)}
              style={{
                borderColor: filterType === type ? getTypeColor(type) : '#e2e8f0',
                color: filterType === type ? getTypeColor(type) : '#4a5568'
              }}
            >
              {getTypeLabel(type)} ({count})
            </button>
          ))}
        </div>
        <div className={styles.filterInfo}>
          {configId && <span className={styles.filterTag}>Config: {configId.substring(0, 12)}...</span>}
          {kbId && <span className={styles.filterTag}>KB: {kbId}</span>}
          {statusCode && <span className={styles.filterTag}>Status: {statusCode}</span>}
        </div>
      </div>

      {/* Error State */}
      {error && (
        <div className={styles.errorContainer}>
          <span className={styles.errorIcon}>⚠️</span>
          <span>{error}</span>
        </div>
      )}

      {/* Templates Grid */}
      {!error && filteredTemplates.length === 0 ? (
        <div className={styles.emptyState}>
          <div className={styles.emptyIcon}>📭</div>
          <h3>No response templates found</h3>
          <p>Try adjusting your filters or create a new template to format your API responses.</p>
          <button onClick={handleCreateTemplate} className={styles.createButton}>
            ✨ Create Response Template
          </button>
        </div>
      ) : (
        <div className={styles.templateGrid}>
          {filteredTemplates.map((template) => (
            <div
              key={template.id}
              className={styles.templateCard}
              onClick={() => handleTemplateClick(template)}
            >
              <div className={styles.templateHeader}>
                <div className={styles.templateBadges}>
                  <span
                    className={styles.typeBadge}
                    style={{
                      backgroundColor: getTypeColor(template._type),
                      color: 'white'
                    }}
                  >
                    {getTypeLabel(template._type)}
                  </span>
                  <span
                    className={styles.statusBadge}
                    style={{
                      backgroundColor: getStatusBadgeColor(template.status_code),
                      color: 'white'
                    }}
                  >
                    {template.status_code}
                  </span>
                </div>
                <span className={styles.templateId}>ID: {template.id.substring(0, 12)}...</span>
              </div>

              <div className={styles.templateBody}>
                <div className={styles.templateInfo}>
                  <div className={styles.templateField}>
                    <span className={styles.fieldLabel}>Config ID:</span>
                    <span className={styles.fieldValue}>
                      {template.config_id ? `${template.config_id.substring(0, 12)}...` : 'N/A'}
                    </span>
                  </div>
                  <div className={styles.templateField}>
                    <span className={styles.fieldLabel}>KB ID:</span>
                    <span className={styles.fieldValue}>{template.kb_id || 'Global'}</span>
                  </div>
                  <div className={styles.templateField}>
                    <span className={styles.fieldLabel}>Created:</span>
                    <span className={styles.fieldValue}>
                      {template.created_at ? new Date(template.created_at * 1000).toLocaleDateString() : 'N/A'}
                    </span>
                  </div>
                </div>

                <div className={styles.templatePreview}>
                  <span className={styles.previewLabel}>Message Format:</span>
                  <div className={styles.previewContent}>
                    {typeof template.resp_template === 'object'
                      ? JSON.stringify(template.resp_template, null, 2).substring(0, 150) + '...'
                      : (template.resp_template || '').substring(0, 150) + '...'}
                  </div>
                </div>
              </div>

              <div className={styles.templateFooter}>
                <button
                  className={styles.viewButton}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleTemplateClick(template);
                  }}
                >
                  View in Preview Tester
                </button>
                {template.controls && (
                  <span className={styles.controlsBadge}>
                    🎛️ {Object.keys(template.controls).length} controls
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Summary */}
      <div className={styles.summary}>
        <div className={styles.summaryItem}>
          <span className={styles.summaryLabel}>Total Templates:</span>
          <span className={styles.summaryValue}>{templates.length}</span>
        </div>
        <div className={styles.summaryItem}>
          <span className={styles.summaryLabel}>Local App:</span>
          <span className={styles.summaryValue}>{typeCounts.local_app || 0}</span>
        </div>
        <div className={styles.summaryItem}>
          <span className={styles.summaryLabel}>Default App:</span>
          <span className={styles.summaryValue}>{typeCounts.default_app || 0}</span>
        </div>
        <div className={styles.summaryItem}>
          <span className={styles.summaryLabel}>Global:</span>
          <span className={styles.summaryValue}>{typeCounts.default_global || 0}</span>
        </div>
      </div>
    </div>
  );
};

export default ResponseTemplates;